'use client';

import { useEffect } from 'react';
import { HomeContextProvider } from 'components/uh-home/HomeContext';
import HomePage from 'components/uh-home/HomePage';
import { getCountrySpecificConfig } from 'lib/region/getCountrySpecificConfig';
import { Currency } from 'utils/Currency';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';

import { JsonLd } from 'components/shared';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { BentoSection } from 'components/uh-home/BentoSection';
import { BottomBar } from 'components/uh-home/BottomBar';
import HomeFaq from 'components/uh-home/faq';
import { ImageTextBlockLayout } from 'components/uh-home/ImageTextBlockLayout';
import { InTheBox } from 'components/uh-home/InTheBox';
import TechSpecs from 'components/uh-home/TechSpecComponent';
import { ProductWizard } from 'components/shared/ProductWizard';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import SleepFeatureCards from 'components/uh-home/SleepFeatureCards';
import { MasterTimelineProvider } from 'contexts/MasterTimelineContext';
import { getHomePricingSchemas } from 'lib/seo/meta/pricing/home';

// Define type for page props
interface HomeBuyPageProps {
  currency: Currency;
  store: ShopifyStore;
  countryCode: string;
  directCheckout?: boolean;
}

// Main page component
const HomeBuyPage = ({
  currency,
  store,
  countryCode,
  directCheckout,
}: HomeBuyPageProps) => {
  const { setStore } = useCartStore(
    useShallow((s) => ({ setStore: s.setStore })),
  );
  useEffect(() => {
    setStore(store);
  }, [setStore, store]);
  useGlobalUI({ headerCollaspable: true });

  const countrySpecificConfig = getCountrySpecificConfig(countryCode);
  const homeSchemas = getHomePricingSchemas({ countryCode, store, currency });

  return (
    <>
      <JsonLd data={homeSchemas} />

      {/* Context provider with optimized props */}
      <HomeContextProvider
        currency={currency}
        store={store}
        defaultAddon={countrySpecificConfig.defaultAddon}
        directCheckout={directCheckout}
      >
        <MasterTimelineProvider>
          <HomePage
            countryCode={countryCode}
            countrySpecificConfig={countrySpecificConfig}
          />
          <InTheBox htmlId="uh-home-contents" />
          <SleepFeatureCards />
          <ImageTextBlockLayout htmlId="uh-home-image-text-block" />
          <BentoSection />
          <TechSpecs />
          <ProductWizard title="Still unsure if Ultrahuman Home is right for you?" />
          <HomeFaq />
          <BottomBar />
        </MasterTimelineProvider>
      </HomeContextProvider>
    </>
  );
};

export default HomeBuyPage;
